/* Creative tools: visual references, retention analysis and market-specific decisions. */
(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wireTabs = (host, selector, select, signal) => {
    const tabs = [...host.querySelectorAll(selector)];
    const activate = (index, focus = false) => {
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      select(index);
      if (focus) tabs[index].focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index), { signal });
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== undefined) { event.preventDefault(); activate(next, true); }
      }, { signal });
    });
    activate(0);
  };
  class CreativeReview extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frames);
      this.controller = new AbortController();
      this.frames = 0;
      this.elapsed = 0;
      this.current = -1;
      this.visible = false;
      this.duration = 3200;
      this.images = [...this.querySelectorAll('[data-review-image]')];
      this.tabs = [...this.querySelectorAll('[data-review-tab]')];
      this.path = this.querySelector('[data-review-curve]');
      this.point = this.querySelector('[data-review-point]');
      this.cursor = this.querySelector('[data-review-cursor]');
      this.time = this.querySelector('[data-review-time]');
      if (!this.images.length || !this.tabs.length || !this.path) return;
      // Key the readout to the actual film, so reordering ads cannot recycle its graph.
      this.profiles = {
        'ugc-deep-6': { hook: 38.7, hold: 21.4, ctr: 1.84, curve: [18,35,51,61,70,80,88,94,100] },
        'pov-104': { hook: 33.2, hold: 17.8, ctr: 1.67, curve: [18,43,61,72,87,93,102,109,114] },
        'pov-102': { hook: 42.6, hold: 26.3, ctr: 2.17, curve: [18,27,40,49,56,65,73,79,85] },
        'studio-4': { hook: 35.4, hold: 22.7, ctr: 1.93, curve: [18,37,48,56,64,72,82,89,96] },
        'pov-103': { hook: 29.8, hold: 14.6, ctr: 1.38, curve: [18,48,68,82,92,105,113,120,126] },
        'studio-2': { hook: 32.9, hold: 19.2, ctr: 1.58, curve: [18,40,57,67,79,87,96,104,110] },
        'ugc-deep-5': { hook: 40.3, hold: 24.8, ctr: 2.06, curve: [18,29,43,53,61,70,78,85,90] },
        'pinterest-film': { hook: 36.8, hold: 20.6, ctr: 1.76, curve: [18,33,50,64,73,82,92,99,105] },
        'studio-3': { hook: 44.1, hold: 28.5, ctr: 2.31, curve: [18,25,36,45,52,59,67,73,79] }
      };
      this.percent = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 2 }).format(value) + '%';
      wireTabs(this, '[data-review-tab]', index => {
        const format = this.tabs[index].dataset.reviewFormatTab;
        const candidates = this.images.map((image, i) => image.dataset.reviewFormat === format ? i : -1).filter(i => i >= 0);
        const next = candidates.find(i => i > this.current) ?? candidates[0];
        if (next !== undefined) this.selectCreative(next);
      }, this.controller.signal);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: .1 });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', () => this.sync(), { signal: this.controller.signal });
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => this.sync(), { signal: this.controller.signal });
    }
    selectCreative(index) {
      const previous = this.images[this.current];
      const image = this.images[index];
      if (!image) return;
      this.swap?.cancel();
      this.current = index;
      this.elapsed = 0;
      this.images.forEach(item => {
        item.hidden = item !== image;
        item.style.zIndex = '0';
      });
      if (previous && previous !== image && this.visible && !reduced()) {
        previous.hidden = false;
        image.style.zIndex = '1';
        this.swap = image.animate([
          { opacity: 0, transform: 'translateY(14px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
        this.swap.finished.then(() => {
          if (previous !== this.images[this.current]) previous.hidden = true;
        }).catch(() => {});
      }
      const tab = this.tabs.find(item => item.dataset.reviewFormatTab === image.dataset.reviewFormat) || this.tabs[0];
      this.tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      this.querySelector('[data-review-tag]').textContent = tab.textContent.trim();
      this.querySelector('[data-review-panel]').setAttribute('aria-labelledby', tab.id);
      this.querySelector('[data-review-insight]').textContent = image.dataset.reviewFinding;
      this.querySelector('[data-review-count]').textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.images.length).padStart(2, '0')}`;
      const row = this.profiles[image.dataset.reviewCreative];
      ['hook', 'hold', 'ctr'].forEach(key => { this.querySelector(`[data-review-${key}]`).textContent = this.percent(row[key]); });
      this.curveFrom = this.curveCurrent || row.curve;
      this.curveTarget = row.curve;
      this.curveCurrent = !this.visible || reduced() ? row.curve : this.curveFrom;
      this.path.setAttribute('d', this.curvePath(this.curveCurrent));
      if (reduced()) this.elapsed = this.duration;
      this.renderScan();
      this.sync();
    }
    curvePath(values) {
      const points = values.map((y, index) => ({ x: 8 + index * 33, y }));
      return points.reduce((path, point, index) => {
        if (!index) return `M${point.x} ${point.y.toFixed(2)}`;
        const before = points[Math.max(0, index - 2)], previous = points[index - 1];
        const after = points[Math.min(points.length - 1, index + 1)];
        const c1x = previous.x + (point.x - before.x) / 6;
        const c1y = previous.y + (point.y - before.y) / 6;
        const c2x = point.x - (after.x - previous.x) / 6;
        const c2y = point.y - (after.y - previous.y) / 6;
        return path + `C${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${point.x} ${point.y.toFixed(2)}`;
      }, '');
    }
    renderScan() {
      const morph = reduced() ? 1 : 1 - Math.pow(1 - Math.min(1, this.elapsed / 480), 3);
      this.curveCurrent = this.curveTarget.map((value, i) => this.curveFrom[i] + (value - this.curveFrom[i]) * morph);
      this.path.setAttribute('d', this.curvePath(this.curveCurrent));
      const ratio = Math.min(1, this.elapsed / this.duration);
      const length = this.path.getTotalLength();
      this.path.style.strokeDasharray = `${length} ${length}`;
      this.path.style.strokeDashoffset = String(length * (1 - ratio));
      const point = this.path.getPointAtLength(length * ratio);
      this.point.setAttribute('cx', point.x.toFixed(2));
      this.point.setAttribute('cy', point.y.toFixed(2));
      this.cursor.setAttribute('d', `M${point.x.toFixed(2)} 12V140`);
      this.time.textContent = `${String(Math.round(ratio * 30)).padStart(2, '0')}s`;
    }
    sync() {
      cancelAnimationFrame(this.frames);
      this.frames = 0;
      this.last = null;
      const playing = this.visible && !document.hidden && !reduced();
      this.images.forEach((video, index) => {
        if (index === this.current && playing) video.play().catch(() => {});
        else video.pause();
      });
      if (!playing) return;
      // Only prepare the next ad, keeping the first view light on mobile.
      const next = this.images[(this.current + 1) % this.images.length];
      if (next && !next.dataset.reviewPrepared) {
        next.dataset.reviewPrepared = 'true';
        next.preload = 'auto';
        next.load();
      }
      const tick = now => {
        if (this.last !== null) this.elapsed += Math.min(100, now - this.last);
        this.last = now;
        if (this.elapsed >= this.duration) {
          this.selectCreative((this.current + 1) % this.images.length);
          return;
        }
        this.renderScan();
        this.frames = requestAnimationFrame(tick);
      };
      this.frames = requestAnimationFrame(tick);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frames);
      this.swap?.cancel();
      this.observer?.disconnect();
      this.controller?.abort();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  }

  class CreativeFormats extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      wireTabs(this, '[data-format-tab]', index => {
        this.querySelectorAll('[data-format-panel]').forEach((panel, i) => {
          panel.hidden = i !== index;
          panel.classList.remove('is-entering');
          if (i === index) panel.classList.add('is-entering');
          panel.querySelectorAll('video').forEach(video => {
            video.muted = true;
            if (i === index && this.visible && !document.hidden && !reduced()) video.play().catch(() => {});
            else video.pause();
          });
        });
      }, this.controller.signal);
      const sync = () => this.querySelectorAll('video').forEach(video => {
        if (!video.closest('[data-format-panel]').hidden && this.visible && !document.hidden && !reduced()) video.play().catch(() => {});
        else video.pause();
      });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; sync(); }, { threshold: .1 });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', sync, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); this.querySelectorAll('video').forEach(video => video.pause()); }
  }
  class CreativeMarkets extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const copy = JSON.parse(this.querySelector('[data-market-copy]').textContent);
      const markets = [
        { winner: 0, hook: [38.7, 31.8, 34.2], ctr: [1.84, 1.42, 1.61], cpa: [18.46, 24.38, 21.74], score: [88, 58, 71], currency: 'EUR' },
        { winner: 1, hook: [29.4, 42.1, 32.8], ctr: [1.29, 2.13, 1.56], cpa: [27.63, 19.81, 24.27], score: [49, 91, 66], currency: 'EUR' },
        { winner: 2, hook: [33.6, 30.2, 46.8], ctr: [1.53, 1.34, 2.42], cpa: [31.72, 36.48, 24.93], score: [63, 47, 94], currency: 'USD' }
      ];
      const locale = document.documentElement.lang || 'en';
      const percent = value => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value) + '%';
      wireTabs(this, '[data-market-tab]', index => {
        const data = markets[index];
        this.querySelectorAll('[data-market-card]').forEach((card, i) => {
          card.dataset.winner = String(data.winner === i);
          card.querySelector('[data-market-hook]').textContent = percent(data.hook[i]);
          card.querySelector('[data-market-ctr]').textContent = percent(data.ctr[i]);
          card.querySelector('[data-market-cpa]').textContent = new Intl.NumberFormat(locale, { style: 'currency', currency: data.currency }).format(data.cpa[i]);
          card.querySelector('[data-market-decision]').textContent = data.winner === i ? copy.winner : copy.iterate;
          card.querySelector('[data-market-score]').style.width = `${data.score[i]}%`;
        });
        this.querySelector('[data-market-insight]').textContent = copy.insights[index];
        this.querySelector('[data-market-panel]').setAttribute('aria-labelledby', this.querySelectorAll('[data-market-tab]')[index].id);
      }, this.controller.signal);
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.syncMedia(); }, { threshold: .1 });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', () => this.syncMedia(), {signal:this.controller.signal});
      matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => this.syncMedia(), {signal:this.controller.signal});
    }
    syncMedia() {
      this.querySelectorAll('video').forEach(video => {
        if (this.visible && !document.hidden && !reduced()) video.play().catch(() => {});
        else video.pause();
      });
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.querySelectorAll('video').forEach(video => video.pause()); }
  }
  if (!customElements.get('bc-creative-review')) customElements.define('bc-creative-review', CreativeReview);
  if (!customElements.get('bc-creative-formats')) customElements.define('bc-creative-formats', CreativeFormats);
  if (!customElements.get('bc-creative-markets')) customElements.define('bc-creative-markets', CreativeMarkets);
})();
